import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, TrendingUp, Phone, Mail, Calendar, Sparkles, Users, Gift, 
  Settings, Lock, Edit, LayoutDashboard, Clock
} from "lucide-react";
import { getCategoryProgramType, getProgramBranding, getProgramLogoUrl } from "@/config/programBranding";
import { BookingActivationCard } from "@/components/booking/BookingActivationCard";

interface LeadsSidebarProps {
  listingId: string;
  listingType?: string;
  isOwnListing?: boolean;
  isAdmin?: boolean;
  isPremium?: boolean;
  editPath?: string;
  listingName?: string;
}

const LeadsSidebar = ({ 
  listingId, 
  listingType = '',
  isOwnListing = false, 
  isAdmin = false,
  isPremium = false,
  editPath = `/listing/${listingId}/edit`,
  listingName = ''
}: LeadsSidebarProps) => {
  const navigate = useNavigate();
  
  const canManage = isOwnListing || isAdmin;
  const hasAccess = canManage && isPremium;

  // Get program branding based on listing type
  const programType = getCategoryProgramType(listingType);
  const programBranding = getProgramBranding(programType);
  const logoUrl = getProgramLogoUrl(programType);

  return (
    <div className="space-y-4">
      {/* Owner/Admin Management Access - Top Priority */}
      {canManage && (
        <Card className={`border-2 overflow-hidden relative ${
          hasAccess 
            ? "border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background" 
            : "border-muted bg-muted/30 opacity-80"
        }`}>
          {hasAccess && (
            <>
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full -mr-16 -mt-16 blur-xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/15 rounded-full -ml-12 -mb-12 blur-lg" />
            </>
          )}
          
          <CardHeader className="relative pb-2">
            <Badge 
              className={`w-fit mb-2 ${
                hasAccess 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted-foreground/20 text-muted-foreground"
              }`}
            >
              {hasAccess ? (
                <>
                  <Settings className="w-3 h-3 mr-1" />
                  Panel de Gestión
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3 mr-1" />
                  Acceso Bloqueado
                </>
              )}
            </Badge>
            <CardTitle className="text-lg">
              {hasAccess ? "Gestiona tu Negocio" : "Sistema de Reservas"}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="relative space-y-3">
            {hasAccess ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Accede al panel de control para gestionar reservas, servicios y disponibilidad.
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(editPath)}
                    className="w-full"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => navigate(`/dashboard/gym/reservas?id=${listingId}`)}
                    className="w-full"
                  >
                    <LayoutDashboard className="w-3 h-3 mr-1" />
                    Reservas
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/dashboard/gym/servicios?id=${listingId}`)}
                    className="w-full text-xs"
                  >
                    <Calendar className="w-3 h-3 mr-1" />
                    Servicios
                  </Button>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/dashboard/gym/disponibilidad?id=${listingId}`)}
                    className="w-full text-xs"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Horarios
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Activa el sistema de reservas para gestionar citas y pagos online.
                </p>
                
                <div className="space-y-1.5 opacity-60">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Calendario inteligente</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>Recordatorios automáticos</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <span>Stripe & PayPal integrado</span>
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => navigate('/premium-upgrade')}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Activar 90 Días Gratis
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Booking Activation Request - For Visitors (non-owners) */}
      {!canManage && (
        <BookingActivationCard listingId={listingId} listingName={listingName} />
      )}

      {/* Booking System Promotion - For Visitors (non-owners) */}
      {!canManage && (
        <Card className="border-2 border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden relative">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/20 rounded-full -mr-20 -mt-20 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/15 rounded-full -ml-16 -mb-16 blur-xl" />
          
          <CardHeader className="relative pb-2">
            {/* Program Branding */}
            {programBranding && (
              <div className="flex items-center gap-2 mb-3">
                {logoUrl && (
                  <img 
                    src={logoUrl} 
                    alt={programBranding.name} 
                    className="h-6 w-auto"
                  />
                )}
                <span className="text-xs text-muted-foreground font-medium">
                  Parte de {programBranding.name}
                </span>
              </div>
            )}
            <Badge className="w-fit mb-2 bg-primary text-primary-foreground animate-pulse">
              <Sparkles className="w-3 h-3 mr-1" />
              90 Días Gratis
            </Badge>
            <CardTitle className="text-xl">
              Sistema de Reservas
            </CardTitle>
          </CardHeader>
          
          <CardContent className="relative space-y-3">
            <p className="text-sm text-muted-foreground">
              Gestiona citas online con pagos integrados. ¡Sin comisiones!
            </p>
            
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-primary" />
                <span>Calendario inteligente</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-primary" />
                <span>Recordatorios automáticos</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span>Stripe & PayPal integrado</span>
              </div>
            </div>

            <Button 
              className="w-full" 
              size="lg"
              onClick={() => navigate('/become-trainer')}
            >
              Empezar Gratis
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Free Professional Profiles */}
      <Card className="border border-accent/30 bg-gradient-to-br from-accent/10 to-background overflow-hidden relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 rounded-full -mr-12 -mt-12" />
        
        <CardHeader className="relative pb-2">
          <Badge variant="outline" className="w-fit mb-2 border-accent/50 text-accent-foreground">
            <Gift className="w-3 h-3 mr-1" />
            Perfil Gratuito
          </Badge>
          <CardTitle className="text-lg">
            ¿Eres Profesional?
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative space-y-3">
          <p className="text-sm text-muted-foreground">
            Crea tu perfil gratis como entrenador, nutricionista o fisioterapeuta.
          </p>
          
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-xs">Entrenadores</Badge>
            <Badge variant="secondary" className="text-xs">Nutricionistas</Badge>
            <Badge variant="secondary" className="text-xs">Fisioterapeutas</Badge>
          </div>

          <Button 
            variant="outline"
            className="w-full border-accent/50 hover:bg-accent/10" 
            onClick={() => navigate('/submit-listing')}
          >
            <Users className="w-4 h-4 mr-2" />
            Registrar Perfil
          </Button>
        </CardContent>
      </Card>

      {/* Leads System - Secondary */}
      <Card className="border border-border/50 bg-muted/30 overflow-hidden relative">
        <CardHeader className="relative pb-2">
          <Badge variant="secondary" className="w-fit mb-2">
            <Zap className="w-3 h-3 mr-1" />
            Sistema de Leads
          </Badge>
          <CardTitle className="text-lg">
            {isOwnListing ? "Activa contactos" : "Leads directos"}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative space-y-3">
          <p className="text-xs text-muted-foreground">
            {isOwnListing 
              ? "Compra leads para recibir contactos directos."
              : "Gimnasios con leads activos reciben contactos directos."}
          </p>
          
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <Phone className="w-3 h-3 text-muted-foreground" />
              <span>Contacto telefónico</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Mail className="w-3 h-3 text-muted-foreground" />
              <span>Email instantáneo</span>
            </div>
          </div>

          <Button 
            variant="ghost"
            className="w-full text-sm" 
            size="sm"
            onClick={() => navigate('/comprar-leads')}
          >
            {isOwnListing ? "Comprar Leads" : "Más información"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadsSidebar;
